import type { GetStaticProps } from "next"
import { LoadingIndicatorBox } from "@/components/resources/components/LoadingIndicatorBox"
import { PageMeta } from "@/components/resources/components/Seo/PageMeta"
import {
  type AuthorPostsProps,
  getAuthorPaths,
  getPostsByAuthor,
} from "@/components/resources/data/sanity/posts"
import { SiteLayout } from "@/components/resources/layouts/site"
import { AuthorPage } from "@/components/resources/templates/AuthorPage"
import { shouldSkipStaticBuild } from "@/components/resources/utils/build"

const AuthorPosts = (props: AuthorPostsProps) => {
  if (!props.author || !props.posts) {
    return <LoadingIndicatorBox />
  }
  return (
    <>
      <PageMeta
        meta={{
          title: `Posts by ${props.author.nickname}`,
          description: `Posts by ${props.author.nickname}`,
          image: props.author.image,
        }}
      />
      <AuthorPage {...props} />
    </>
  )
}

export async function getStaticPaths() {
  if (shouldSkipStaticBuild()) {
    return {
      paths: [],
      fallback: true,
    }
  }

  const paths = await getAuthorPaths()

  return {
    paths: paths.map(({ slug }) => ({ params: { slug } })),
    fallback: true, // Allow publishing new authors without build
  }
}

export const getStaticProps: GetStaticProps<AuthorPostsProps, { slug: string }> = async ({
  params,
}) => {
  const { slug } = params!
  const authorData = await getPostsByAuthor(slug)

  if (!authorData) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      author: authorData.author,
      posts: authorData.posts,
    },
    revalidate: 180, // Revalidate every 3 minutes
  }
}

AuthorPosts.getLayout = SiteLayout

export default AuthorPosts
